import { createCriteriaParameters, standardDeleteQuery, standardGetQuery, standardPostQuery, standardPutQuery } from "./apiUtils.js";

import { replacePlaceholdersWithObjValues, setDeleteArrayState, setInsertArrayState } from "../shared/miscUtils.js";

import { SNACKBAR_SUCCESS_AUTHOR_ALIASES_APPLIED, SNACKBAR_SUCCESS_AUTHOR_ALIASES_NONE, SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_APPLIED, SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_NONE, SNACKBAR_SUCCESS_CREATED, SNACKBAR_SUCCESS_DELETED, SNACKBAR_SUCCESS_UPDATED } from "../../config/language/english.js";
import * as Constants from "../../config/constants/Constants.js";

/**
 * Function for querying authors with filters, pagination, sorting etc.
 */
export const queryAuthors = (props, options = {}) => {
    const path = Constants.API_AUTHORS + createCriteriaParameters(options);

    standardGetQuery(props, path, (response) => {
        const states = props.backend.authors;

        setInsertArrayState(states.setData, response.body.elements);
        states.setQueryLoading(false);

        if (options.pagination === false) return;

        if (options.page !== undefined) states.setQueriedUntil(options.page);
        if (response?.body?.total !== undefined) states.setRowCount(response.body.total);
    });
};

/**
 * Function for querying authors with filters, pagination, sorting etc.
 * NB! This is only meant for author selection pop-up.
 */
export const querySelectAuthors = (props, states = {}, options = {}) => {
    const params = createCriteriaParameters(options);
    const path = Constants.API_AUTHORS + params + (params ? "&" : "?") + "type=select";

    standardGetQuery(props, path, (response) => {
        setInsertArrayState(states.setData, response.body.elements);
    });
};

/**
 * Function for querying an existing author.
 */
export const queryAuthor = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_AUTHORS_ID, { author_id: options.id });

    standardGetQuery(props, path, (response) => {
        const states = props.backend.authors;

        setInsertArrayState(states.setData, [{ ...response.body }]);
    });
};

/**
 * Function for creating a new author.
 */
export const createAuthor = (props, options = {}) => {
    const path = Constants.API_AUTHORS;

    standardPostQuery(props, path, options.body, (response) => {
        const states = props.backend.authors;

        setInsertArrayState(states.setData, [{ ...response.body }]);
        states.setRowCount((prev) => prev + 1);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_CREATED });
    });
};

/**
 * Function for updating a author.
 */
export const updateAuthor = (props, options = {}, callback = null) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_AUTHORS_ID, { author_id: options.id });

    standardPutQuery(props, path, options.body, (response) => {
        const states = props.backend.authors;

        setInsertArrayState(states.setData, [{ ...response.body }]);

        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_UPDATED });
        if (callback) callback(response);
    });
};

/**
 * Function for deleting author(s).
 */
export const deleteAuthors = (props, options = {}) => {
    const path = Constants.API_AUTHORS;

    standardDeleteQuery(props, path, options.body, (response) => {
        const states = props.backend.authors;

        setDeleteArrayState(states.setData, options.ids);

        states.setRowCount(states.rowCount - options.ids.length);
        states.setSelected([]);

        props.showSnackbar({ message: SNACKBAR_SUCCESS_DELETED });
    });
};

/**
 * Unlink an author from resources that only matched excluded phrases.
 */
export const applyAuthorExclusions = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_AUTHORS_ID_APPLY_EXCLUSIONS, { author_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.authors;
        states.setSelected([]);

        const unlinked = response.body?.unlinked || 0;
        props.showSnackbar({
            message: unlinked ? SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_APPLIED : SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_NONE
        });
    });
};

/**
 * Unlink authors from resources that only matched excluded phrases.
 */
export const applyAuthorsExclusions = (props, options = {}) => {
    standardPutQuery(props, Constants.API_AUTHORS_APPLY_EXCLUSIONS, options.body, (response) => {
        const states = props.backend.authors;
        states.setSelected([]);

        const unlinked = (response.body || []).reduce((sum, item) => sum + (item.unlinked || 0), 0);
        props.showSnackbar({
            message: unlinked ? SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_APPLIED : SNACKBAR_SUCCESS_AUTHOR_EXCLUSIONS_NONE
        });
    });
};

/**
 * Link an author to unlinked resources matching their name or aliases after exclusions.
 */
export const applyAuthorAliases = (props, options = {}) => {
    const path = replacePlaceholdersWithObjValues(Constants.API_AUTHORS_ID_APPLY_ALIASES, { author_id: options.id });

    standardPutQuery(props, path, undefined, (response) => {
        const states = props.backend.authors;
        states.setSelected([]);

        const linked = response.body?.linked || 0;
        props.showSnackbar({
            message: linked ? SNACKBAR_SUCCESS_AUTHOR_ALIASES_APPLIED : SNACKBAR_SUCCESS_AUTHOR_ALIASES_NONE
        });
    });
};

/**
 * Link authors to unlinked resources matching their name or aliases after exclusions.
 */
export const applyAuthorsAliases = (props, options = {}) => {
    standardPutQuery(props, Constants.API_AUTHORS_APPLY_ALIASES, options.body, (response) => {
        const states = props.backend.authors;
        states.setSelected([]);

        const linked = (response.body || []).reduce((sum, item) => sum + (item.linked || 0), 0);
        props.showSnackbar({
            message: linked ? SNACKBAR_SUCCESS_AUTHOR_ALIASES_APPLIED : SNACKBAR_SUCCESS_AUTHOR_ALIASES_NONE
        });
    });
};
